import { Inject, Injectable, InjectionToken, NgZone, OnDestroy } from '@angular/core';
import { AppSessionService } from '@shared/common/session/app-session.service';
import {
    DiscoveryEntryPoint,
    DiscoveryJobDto,
    DiscoveryJobsServiceProxy,
    ScanMomentDto,
} from '@shared/service-proxies/service-proxies';
import {
    asyncScheduler,
    BehaviorSubject,
    defer,
    EMPTY,
    merge,
    Observable,
    of,
    SchedulerLike,
    Subject,
    timer,
} from 'rxjs';
import {
    auditTime,
    catchError,
    distinctUntilChanged,
    expand,
    filter,
    finalize,
    ignoreElements,
    map,
    shareReplay,
    startWith,
    switchMap,
    take,
    takeUntil,
} from 'rxjs/operators';
import {
    acceptDiscoveryJobSignal,
    DiscoveryJobChangedSignal,
    isActiveJobStatus,
    isDiscoveryEntryPoint,
} from './discovery-job-signal';

/** How long after an answer the next `GetCurrent` is sent while the job is active (queued or running). */
export const DISCOVERY_JOB_POLL_MS = 3000;

/** Pushes for one entry point arriving within this window are coalesced into one re-read. */
export const DISCOVERY_JOB_SIGNAL_AUDIT_MS = 250;

/**
 * After a failed `GetCurrent` of an active job, the next background read waits this long instead of the poll interval
 * (ABP's interceptor already showed its dialog; a push or `refresh` still reads at once).
 */
export const DISCOVERY_JOB_ERROR_PAUSE_MS = 30000;

/**
 * The scheduler the service's timers run on: RxJS's `asyncScheduler` (real time), unless a spec provides a virtual one.
 */
export const DISCOVERY_JOB_SCHEDULER = new InjectionToken<SchedulerLike>('DISCOVERY_JOB_SCHEDULER', {
    providedIn: 'root',
    factory: () => asyncScheduler,
});

/** One step of the poll chain: whether to read again, and after how long. */
interface PollStep {
    readonly again: boolean;
    readonly delayMs: number;
}

/** What the service keeps per entry point. */
interface JobEntry {
    readonly entryPoint: DiscoveryEntryPoint;
    /** The last server answer applied (null before the first one). Survives the last watcher leaving. */
    readonly latest$: BehaviorSubject<DiscoveryJobDto | null>;
    /** Re-read now (accept, refresh). */
    readonly reread$: Subject<void>;
    /** Re-read after the push audit window (acceptSignal). */
    readonly signal$: Subject<void>;
    /** The one shared watch stream of this entry point (set once, right after the entry is created). */
    stream$: Observable<DiscoveryJobDto>;
    /** Whether the shared stream has at least one subscriber (its poll chain is running). */
    watching: boolean;
}

/** The time of an ISO string in ms, or NaN when it is missing or not a date. */
function timeOf(iso: string | null | undefined): number {
    return iso ? Date.parse(iso) : NaN;
}

/** The last moment's sequence of a job, or 0 when it has none. */
function lastMomentSequence(moments: ScanMomentDto[] | null | undefined): number {
    if (!moments || moments.length === 0) {
        return 0;
    }
    return moments[moments.length - 1].sequence || 0;
}

/**
 * Whether two answers show the same job state, so the second one is not emitted again: the same job, status, stage,
 * outcome, failure code, fixture flag, completion time and moments (count and last sequence). `AsOfUtc` is left out:
 * the server stamps every answer with its own clock, and a new stamp alone changes nothing on screen (IN-P-04).
 */
export function sameJobSnapshot(a: DiscoveryJobDto | null | undefined, b: DiscoveryJobDto | null | undefined): boolean {
    if (!a || !b) {
        return a === b;
    }
    return (
        (a.jobId || 0) === (b.jobId || 0) &&
        a.status === b.status &&
        a.stage === b.stage &&
        a.outcome === b.outcome &&
        (a.failureCode || null) === (b.failureCode || null) &&
        !!a.isFixture === !!b.isFixture &&
        (a.completedAtUtc || null) === (b.completedAtUtc || null) &&
        (a.moments ? a.moments.length : 0) === (b.moments ? b.moments.length : 0) &&
        lastMomentSequence(a.moments) === lastMomentSequence(b.moments)
    );
}

/**
 * Whether an answer replaces the one the service holds:
 *  - nothing held yet: yes;
 *  - the same job: yes, unless the answer is older (`AsOfUtc` earlier, both parseable), so an answer that was overtaken
 *    never moves a job back to an earlier status or stage;
 *  - a newer job (higher id): yes;
 *  - an older job (lower id, above 0): no;
 *  - "no job" (id 0): only from a `GetCurrent` read, which is the server's truth for the entry point; a pushed-in answer
 *    with id 0 (StartScan's "start requested, waiting") is never a job and is dropped.
 *
 * @param current the answer the service holds, or null.
 * @param next the new answer.
 * @param pushed true for an answer handed in through `accept` (StartScan / RetryScan), false for a `GetCurrent` read.
 */
export function shouldApplyJob(
    current: DiscoveryJobDto | null | undefined,
    next: DiscoveryJobDto | null | undefined,
    pushed: boolean
): boolean {
    const nextId: number = next ? next.jobId || 0 : 0;
    const currentId: number = current ? current.jobId || 0 : 0;

    if (!next) {
        return false;
    }
    if (pushed && nextId <= 0) {
        return false;
    }
    if (!current) {
        return true;
    }
    if (nextId === currentId) {
        return !(timeOf(next.asOfUtc) < timeOf(current.asOfUtc));
    }
    if (nextId === 0) {
        return !pushed;
    }
    return nextId > currentId;
}

/** Whether a held answer is an active job (queued or running). */
export function isActiveJob(job: DiscoveryJobDto | null | undefined): boolean {
    return !!job && (job.jobId || 0) > 0 && isActiveJobStatus(job.status);
}

/**
 * `DiscoveryJobStatusService` — the one live view of the discovery jobs of this workspace, shared by the onboarding
 * scan screen and the Organization Intelligence page (plan §3.1; ADAPTIVE_ONBOARDING_CONTRACT.md §7).
 *
 * `watch(entryPoint)` answers that entry point's latest job (`DiscoveryJobs.GetCurrent`). While anyone watches:
 *  - it reads at once (so after F5 or a navigation it re-attaches to the same job: OB-S-06, IN-T-10), replaying the last
 *    answer it holds first, so a page that comes back shows the job without a full-page spinner (IN-A-05);
 *  - it reads again `DISCOVERY_JOB_POLL_MS` after each answer, but ONLY while the job is active (queued or running), and
 *    stops on a terminal status or "no job". A queued job that never starts (a fixture job on a host without the fixture
 *    flags) keeps being read for as long as someone watches;
 *  - a `discoveryJobChanged` push for the entry point (forwarded by UserSignalrService to `acceptSignal`) re-reads after a
 *    `DISCOVERY_JOB_SIGNAL_AUDIT_MS` audit window and restarts the poll chain, so a new job started elsewhere is picked
 *    up. The push itself is never shown: only server answers are emitted, so no stage is ever made by a timer.
 * When the last watcher leaves, the chain stops (no background calls); the last answer is kept for the next watcher.
 *
 * Answers are emitted only when the job's visible state changed (`sameJobSnapshot`), and an overtaken answer never
 * replaces a newer one (`shouldApplyJob`). `accept(job)` hands in a StartScan / RetryScan answer so polling begins at
 * once. A failed read keeps the last answer and, for an active job, retries after `DISCOVERY_JOB_ERROR_PAUSE_MS`.
 * Every emission runs inside `NgZone.run`: pushes and their reads start outside Angular's zone.
 *
 * Typed and providedIn 'root', never a `DataService` channel.
 */
@Injectable({ providedIn: 'root' })
export class DiscoveryJobStatusService implements OnDestroy {
    private readonly destroy$ = new Subject<void>();
    private readonly entries = new Map<DiscoveryEntryPoint, JobEntry>();

    constructor(
        private readonly proxy: DiscoveryJobsServiceProxy,
        private readonly session: AppSessionService,
        private readonly zone: NgZone,
        @Inject(DISCOVERY_JOB_SCHEDULER) private readonly scheduler: SchedulerLike
    ) {}

    /**
     * The live latest job of an entry point (see the class summary). Emits the last answer held at once, then every
     * changed server answer. Never errors; completes only when the service is destroyed.
     */
    watch(entryPoint: DiscoveryEntryPoint): Observable<DiscoveryJobDto> {
        return this.entry(entryPoint).stream$;
    }

    /** The last answer held for an entry point, or null before the first one. */
    current(entryPoint: DiscoveryEntryPoint): DiscoveryJobDto | null {
        const entry: JobEntry | undefined = this.entries.get(entryPoint);

        return entry ? entry.latest$.value : null;
    }

    /** Re-reads an entry point at once when someone watches it (otherwise the next `watch` reads anyway). */
    refresh(entryPoint: DiscoveryEntryPoint): void {
        const entry: JobEntry | undefined = this.entries.get(entryPoint);

        if (entry && entry.watching) {
            entry.reread$.next();
        }
    }

    /**
     * Hands in a job answered by a write (StartScan, RetryScan): applied when it is a real job not older than the one
     * held, then, when someone watches, read again at once so polling begins. An answer with job id 0 (the start was
     * requested and waits for another entry point's job to cancel) is ignored.
     */
    accept(job: DiscoveryJobDto | null | undefined): void {
        let entry: JobEntry;

        if (!job || !isDiscoveryEntryPoint(job.entryPoint) || !((job.jobId || 0) > 0)) {
            return;
        }
        entry = this.entry(job.entryPoint);
        this.apply(entry, job, true);
        if (entry.watching) {
            entry.reread$.next();
        }
    }

    /**
     * The `discoveryJobChanged` push, forwarded by UserSignalrService (outside Angular's zone). Accepted only for this
     * workspace (`acceptDiscoveryJobSignal`); then, when the push's entry point is watched, it is re-read after the
     * audit window. The push's own status and stage are never shown.
     *
     * @returns whether the push was accepted.
     */
    acceptSignal(payload: DiscoveryJobChangedSignal | null | undefined): boolean {
        const entry: JobEntry | undefined = payload ? this.entries.get(payload.entryPoint) : undefined;

        if (!acceptDiscoveryJobSignal(payload, this.session.tenantId)) {
            return false;
        }
        if (entry && entry.watching) {
            this.zone.run(() => entry.signal$.next());
        }
        return true;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.entries.forEach((entry: JobEntry) => {
            entry.reread$.complete();
            entry.signal$.complete();
            entry.latest$.complete();
        });
        this.entries.clear();
    }

    /** The entry of an entry point, created on first use. */
    private entry(entryPoint: DiscoveryEntryPoint): JobEntry {
        const existing: JobEntry | undefined = this.entries.get(entryPoint);
        let created: JobEntry;

        if (existing) {
            return existing;
        }
        created = {
            entryPoint,
            latest$: new BehaviorSubject<DiscoveryJobDto | null>(null),
            reread$: new Subject<void>(),
            signal$: new Subject<void>(),
            stream$: EMPTY,
            watching: false,
        };
        created.stream$ = this.buildStream(created);
        this.entries.set(entryPoint, created);
        return created;
    }

    /** The shared watch stream of an entry: the held answers, plus the poll chain while subscribed. */
    private buildStream(entry: JobEntry): Observable<DiscoveryJobDto> {
        return defer(() => {
            const triggers$: Observable<void> = merge(
                entry.reread$,
                entry.signal$.pipe(auditTime(DISCOVERY_JOB_SIGNAL_AUDIT_MS, this.scheduler))
            );
            const driver$: Observable<DiscoveryJobDto> = triggers$.pipe(
                startWith(undefined as void),
                switchMap(() => this.pollChain(entry)),
                ignoreElements()
            );

            entry.watching = true;
            return merge(
                entry.latest$.pipe(filter((job: DiscoveryJobDto | null): job is DiscoveryJobDto => !!job)),
                driver$
            ).pipe(
                finalize(() => {
                    entry.watching = false;
                })
            );
        }).pipe(
            distinctUntilChanged(sameJobSnapshot),
            takeUntil(this.destroy$),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    /** One read, then another after each answer while the job held is active. */
    private pollChain(entry: JobEntry): Observable<PollStep> {
        return this.read(entry).pipe(
            expand((step: PollStep) =>
                step.again
                    ? timer(step.delayMs, this.scheduler).pipe(switchMap(() => this.read(entry)))
                    : EMPTY
            )
        );
    }

    /** One `GetCurrent` of the entry point, applied; never errors. */
    private read(entry: JobEntry): Observable<PollStep> {
        return defer(() => this.proxy.getCurrent(entry.entryPoint)).pipe(
            take(1),
            map((job: DiscoveryJobDto) => {
                this.apply(entry, job, false);
                return { again: isActiveJob(entry.latest$.value), delayMs: DISCOVERY_JOB_POLL_MS };
            }),
            catchError(() => of({ again: isActiveJob(entry.latest$.value), delayMs: DISCOVERY_JOB_ERROR_PAUSE_MS }))
        );
    }

    /** Applies an answer when it replaces the one held (inside the zone). */
    private apply(entry: JobEntry, job: DiscoveryJobDto | null | undefined, pushed: boolean): void {
        if (!job || !shouldApplyJob(entry.latest$.value, job, pushed)) {
            return;
        }
        this.zone.run(() => entry.latest$.next(job));
    }
}
